/**
 * Marketing tracking layer for the rotahq.uk landing page + acquisition flows.
 *
 * Responsibilities:
 *  * UTM attribution: capture utm_* / gclid / fbclid from the URL once and
 *    persist for the session so signup/demo submissions carry attribution.
 *  * Clean dataLayer: every marketing event is pushed as a plain object
 *    ({event, ...params, ...utm}) so Google Tag Manager can consume it
 *    without code changes. GA4 receives the same event via analytics.js.
 *  * Env-gated loaders: GTM (REACT_APP_GTM_ID) and Meta Pixel
 *    (REACT_APP_META_PIXEL_ID) load ONLY when configured — silent no-ops
 *    otherwise. The Meta Pixel additionally waits for cookie consent
 *    (UK PECR) before loading; GTM relies on Google Consent Mode defaults
 *    already set by analytics.js (storage denied until consent).
 *
 * Event vocabulary (fixed — used by Google Ads / Meta campaign setup):
 *   page_view, view_pricing, click_start_trial, click_book_demo,
 *   signup_started, signup_completed, demo_form_started, demo_form_submitted
 */
import { hasAnalyticsConsent, trackEvent } from '@/lib/analytics';

const GTM_ID = process.env.REACT_APP_GTM_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

const UTM_STORAGE_KEY = 'rotahq_utm_v1';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];

// Meta standard-event mapping; everything else goes out as trackCustom.
const META_STANDARD_EVENTS = {
  signup_completed: 'CompleteRegistration',
  demo_form_submitted: 'Lead',
};

// ---------------------------------------------------------------------------
// UTM attribution
// ---------------------------------------------------------------------------

/**
 * Capture attribution params from the current URL (last non-direct touch
 * wins — standard paid-ads convention). Safe to call on every route change:
 * it only writes when at least one attribution param is present.
 */
export function captureUtm(search) {
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    const found = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) found[key] = value.slice(0, 200);
    }
    if (Object.keys(found).length === 0) return;
    const existing = getStoredUtm();
    sessionStorage.setItem(
      UTM_STORAGE_KEY,
      JSON.stringify({ ...existing, ...found, captured_at: new Date().toISOString() }),
    );
  } catch {
    /* storage unavailable (private mode) — attribution is best-effort */
  }
}

/** The persisted attribution params ({} when none). */
export function getStoredUtm() {
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const out = {};
    for (const key of UTM_KEYS) {
      if (typeof parsed[key] === 'string') out[key] = parsed[key];
    }
    return out;
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Tag loaders (env-gated, load-once)
// ---------------------------------------------------------------------------
let gtmLoaded = false;
let pixelLoaded = false;

/** Google Tag Manager — the production container (GTM-MXVTQ8Q3) is loaded
 * directly from public/index.html (agency requirement: "as high in the head
 * as possible", plus the noscript iframe). This loader remains only as an
 * env-gated fallback for alternative containers and NO-OPS when any gtm.js
 * script tag is already present, so the container is never loaded twice. */
export function initGTM() {
  if (gtmLoaded || !GTM_ID || typeof window === 'undefined') return;
  if (document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
    gtmLoaded = true; // already installed via index.html — never double-load
    return;
  }
  gtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
}

/**
 * Meta Pixel — no-op unless REACT_APP_META_PIXEL_ID is configured AND the
 * visitor has accepted the cookie banner (UK PECR). Call again after consent
 * is granted (CookieBanner does this) to load it late.
 */
export function initMetaPixel() {
  if (pixelLoaded || !META_PIXEL_ID || typeof window === 'undefined') return;
  if (!hasAnalyticsConsent()) return;
  pixelLoaded = true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
}

/** Hook for CookieBanner: the moment consent is granted, tags may load. */
export function onMarketingConsentGranted() {
  initMetaPixel();
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------
function pushDataLayer(payload) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

// ---------------------------------------------------------------------------
// Agency conversion events (GTM container GTM-MXVTQ8Q3)
// ---------------------------------------------------------------------------
/**
 * Push one agency-defined conversion event to the GTM dataLayer.
 * The agency's GTM triggers listen for these exact event names and the
 * `event_category` / `event_label` keys, so the payload shape is fixed.
 * dataLayer-only on purpose: the agency forwards these to GA4/Ads inside
 * GTM, so pushing them through gtag here too would double-count.
 */
export function agencyEvent(event, eventCategory, eventLabel, extra = {}) {
  pushDataLayer({
    event,
    event_category: eventCategory,
    event_label: eventLabel,
    ...extra,
  });
}

/**
 * Internal event → agency event aliasing.
 *
 * Our internal names (click_start_trial, trial_started, …) feed existing
 * GA4 reports and MUST NOT be renamed. Whenever one of them fires, this
 * mapper ALSO pushes the agency-named dataLayer event so the agency's GTM
 * triggers fire at exactly the same moment:
 *
 *   click_book_demo              → book_a_demo
 *   click_start_trial (pricing_*) → pricing_button_click (label = plan id)
 *   click_start_trial (other)     → homepage_signup_click
 *   trial_started                 → successful_signup  (the key conversion —
 *                                   fires only after the server confirms the
 *                                   account was created)
 */
function pushAgencyAlias(name, params = {}) {
  if (name === 'click_book_demo') {
    agencyEvent('book_a_demo', 'engagement', 'book_demo_click');
    return;
  }
  if (name === 'click_start_trial') {
    const placement = String(params.placement || '');
    if (placement.startsWith('pricing_')) {
      agencyEvent('pricing_button_click', 'engagement', placement.slice('pricing_'.length));
    } else {
      agencyEvent('homepage_signup_click', 'conversion', 'homepage_cta');
    }
    return;
  }
  if (name === 'trial_started') {
    agencyEvent('successful_signup', 'conversion', 'new_account_created', {
      user_type: 'new_trial',
    });
  }
}

/**
 * Fire one marketing event everywhere at once:
 *   1. dataLayer (GTM) — plain object with utm merged in
 *   2. GA4 (gtag)      — via the existing analytics module
 *   3. Meta Pixel      — standard event where mapped, otherwise trackCustom
 *   4. Agency alias    — the agency-named GTM event, when one is mapped
 */
export function marketingEvent(name, params = {}) {
  const utm = getStoredUtm();
  pushDataLayer({ event: name, ...utm, ...params });
  pushAgencyAlias(name, params);
  trackEvent(name, params);
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    const standard = META_STANDARD_EVENTS[name];
    if (standard) window.fbq('track', standard, params);
    else window.fbq('trackCustom', name, params);
  }
}

/** SPA page view for GTM + Meta Pixel (GA4 page_view is owned by analytics.js). */
let lastMarketingPath = null;

export function marketingPageView(path) {
  // Consecutive-duplicate guard (mirrors analytics.js): StrictMode re-runs
  // and the pixel's own init PageView must not double-count the first load.
  if (path === lastMarketingPath) return;
  const isFirstView = lastMarketingPath === null;
  lastMarketingPath = path;
  pushDataLayer({ event: 'page_view', page_path: path, ...getStoredUtm() });
  // The pixel already fires PageView when it initialises, so only forward
  // subsequent SPA route changes to fbq.
  if (!isFirstView && typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }
}
